/**
 * The Slack announcement editor. The release notes are read by 30–40 people, so
 * the operator gets a block-by-block editor with Slack's own rendering beside each
 * block (`AnnouncementBlockEditor`), the raw JSON a toggle away for anything the
 * blocks cannot express, the identical Block Kit validation the host re-runs (so
 * problems show before the round trip), and three guided actions: save a hand-edit,
 * regenerate from scratch, or approve — which publishes and is the point of no
 * return. Editing and the actions are locked outside the `reviewing` phase using
 * the shared legality table, matching D12 (visible from the draft onwards, editable
 * only during review).
 *
 * Both views edit the one `draft` string, so "unsaved edit" stays an exact
 * comparison against the announcement committed on the host, and what is approved
 * is the file, not a model of it.
 *
 * The lock takes effect from the approve click rather than from the phase change
 * it eventually causes: publishing takes about half a minute on the host and
 * nothing in git changes until the tag lands, so an editor that waits for the
 * facts stays live through the one window where an edit is quietly overtaken by
 * the publish. `phase` closes most of it (an in-flight approval reads as
 * `publishing`); `busy` covers the round trip before that instruction is visible.
 */
import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Grid,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    INSTRUCTION_KIND,
    legalInstructions,
    parseAnnouncementBlocks,
    validateAnnouncementJson,
    type AnnouncementTarget,
    type InstructionKind,
    type Phase,
} from '@finvari/deployer';
import { AnnouncementBlockEditor } from './AnnouncementBlockEditor';
import { AnnouncementPreview } from './AnnouncementPreview';
import { createInstruction } from '../lib/createInstruction';
import { dbForRole } from '../firebase';
import { useOperator } from '../auth';

export interface AnnouncementEditorProps {
    version: string;
    phase: Phase;
    /** The current committed announcement JSON, if generated. */
    json: string | undefined;
    /**
     * Holds the three actions (not the editor) while another instruction is in
     * flight on the host; editing a draft locally is always fine.
     */
    actionsBlockedReason?: string;
    /**
     * The connector or app release this announcement is for, when it is not the
     * server's. Save and Regenerate carry it so the host writes the right file;
     * Approve is not offered, because approving publishes the whole release and
     * belongs to the one editor that is about the release itself.
     */
    target?: AnnouncementTarget;
    /** Heading, when this is not the release's own announcement. */
    title?: string;
    /** One line above the editor saying what this announcement is about. */
    subtitle?: string;
}

/** The operator-facing reason a prepare action is unavailable in this phase, if any. */
function reasonFor(phase: Phase, kind: InstructionKind): string | undefined {
    return legalInstructions(phase, 'prepare').find((l) => l.kind === kind)?.reasonIfIllegal;
}

/** The two ways to edit the draft. Blocks is where the reviewing happens. */
const BLOCKS_VIEW = 'blocks';
const JSON_VIEW = 'json';
type EditorView = typeof BLOCKS_VIEW | typeof JSON_VIEW;

export function AnnouncementEditor({
    version,
    phase,
    json,
    actionsBlockedReason,
    target,
    title = 'Announcement',
    subtitle,
}: AnnouncementEditorProps) {
    const operator = useOperator();
    const [draft, setDraft] = useState(json ?? '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | undefined>();

    // Re-seed the editor when a new announcement arrives (regenerate/first draft),
    // but only when the operator has no unsaved edits to lose.
    useEffect(() => {
        setDraft((current) => (current === '' || current === json ? json ?? current : current));
    }, [json]);

    const validation = useMemo(() => validateAnnouncementJson(draft || '{}'), [draft]);
    const dirty = draft !== (json ?? '');

    const [view, setView] = useState<EditorView>(BLOCKS_VIEW);
    const parses = useMemo(() => parseAnnouncementBlocks(draft || '{}').ok, [draft]);

    // A draft that does not parse has no blocks to show and no way back from the block
    // view, so the JSON box — the only thing that can repair it — takes over until it
    // parses again. The parse error itself comes through the validation alert below.
    useEffect(() => {
        if (!parses) setView(JSON_VIEW);
    }, [parses]);

    const editReason = reasonFor(phase, INSTRUCTION_KIND.prepareAnnouncementSet);
    const approveReason = reasonFor(phase, INSTRUCTION_KIND.prepareApproveAnnouncement) ?? actionsBlockedReason;
    const regenerateReason = reasonFor(phase, INSTRUCTION_KIND.prepareStep) ?? actionsBlockedReason;
    const saveReason = editReason ?? actionsBlockedReason;
    const locked = !!editReason;

    const run = async (kind: InstructionKind, params: Record<string, unknown>) => {
        setBusy(true);
        setError(undefined);
        const result = await createInstruction(dbForRole('prepare'), 'prepare', kind, params, operator, version);
        if (!result.ok) {
            setError(result.error);
            setBusy(false);
            return;
        }
        // An approval that landed is irrevocable and the announcement now belongs
        // to the host, so the editor stays locked from here: the phase only reads
        // `publishing` once the new instruction document has come back through the
        // subscription, and re-enabling the box across that gap invites exactly the
        // edit this lock exists to prevent. The other two actions leave the release
        // in review, where editing is the point.
        if (kind !== INSTRUCTION_KIND.prepareApproveAnnouncement) setBusy(false);
    };

    // Until describe has run there is nothing to show: an empty JSON box beside a
    // blank preview is the largest thing on the page and reads as a broken editor
    // rather than a step that has not happened yet.
    if (json === undefined) {
        return (
            <Stack spacing={1}>
                <Typography variant="h6">{title}</Typography>
                <Typography variant="body2" color="text.secondary">
                    Claude drafts it in the describe step. It appears here for review, editing and approval once
                    that step finishes.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h6">{title}</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={view}
                    onChange={(_e, next: EditorView | null) => next && setView(next)}
                >
                    <ToggleButton value={BLOCKS_VIEW} disabled={!parses}>
                        Blocks
                    </ToggleButton>
                    <ToggleButton value={JSON_VIEW}>JSON</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            {subtitle && (
                <Typography variant="body2" color="text.secondary">
                    {subtitle}
                </Typography>
            )}
            {locked && (
                <Alert severity="info">{editReason}</Alert>
            )}
            {view === BLOCKS_VIEW ? (
                <AnnouncementBlockEditor json={draft} onChange={setDraft} disabled={locked || busy} />
            ) : (
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            label="slack_announce.json"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            multiline
                            minRows={16}
                            fullWidth
                            disabled={locked || busy}
                            slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: 13 } } }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Preview
                        </Typography>
                        <AnnouncementPreview json={draft} />
                    </Grid>
                </Grid>
            )}

            {!validation.ok && (
                <Alert severity="error">
                    {validation.errors.map((e, i) => (
                        <div key={i}>{e}</div>
                    ))}
                </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" spacing={2}>
                <Tooltip title={saveReason ?? ''}>
                    <span>
                        <Button
                            variant="outlined"
                            disabled={!!saveReason || busy || !dirty || !validation.ok}
                            onClick={() => void run(INSTRUCTION_KIND.prepareAnnouncementSet, { json: draft, ...target })}
                        >
                            Save edit
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={regenerateReason ?? 'Re-run Claude from scratch'}>
                    <span>
                        <Button
                            variant="outlined"
                            disabled={busy || !!regenerateReason}
                            onClick={() => void run(INSTRUCTION_KIND.prepareStep, { step: 'describe', ...target })}
                        >
                            Regenerate
                        </Button>
                    </span>
                </Tooltip>
                {/*
                  * Only on the release's own announcement. Approving publishes and
                  * tags the whole release — every announcement in it, including the
                  * connector's — so one button for all of them, on the one editor
                  * that is about the release rather than about a component.
                  */}
                {!target && (
                    <Tooltip title={approveReason ?? 'Publishes the release — cannot be undone'}>
                        <span>
                            <Button
                                variant="contained"
                                color="warning"
                                disabled={busy || !!approveReason || dirty}
                                onClick={() => void run(INSTRUCTION_KIND.prepareApproveAnnouncement, {})}
                            >
                                Approve &amp; publish
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Stack>
            {dirty && !target && !approveReason && (
                <Typography variant="caption" color="text.secondary">
                    Save or discard your edit before approving — approval publishes what is committed on the host.
                </Typography>
            )}
        </Stack>
    );
}
